import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Stack } from '@mui/material';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import ApprovalQueue from './ApprovalQueue';
import ChecklistToolbar from './ChecklistToolbar';
import ContextCard from './ContextCard';
import PlaceholderChecklistTab from './PlaceholderChecklistTab';
import { CM_CHECKLIST_CATEGORIES } from './constants';
import { CapacityReportCard, MarginReportCard } from './reportCards';
import type {
  ApprovalQueueItem,
  ApprovalReportSummary,
  ChecklistCategory,
  ChecklistCategoryId,
  ChecklistRow,
  ChecklistTabId,
  EditedCapacityValues,
  EditedMarginValues,
  ManualApprovalState,
} from './types';
import {
  ChecklistStatus,
  EgsCapacityChecklistRow,
  EgsMarginChecklistRow,
  buildEgsCapacityRows,
  buildEgsMarginRows,
  calculateMarginPercentage,
  formatMoney,
  formatOptionalNumber,
  getMarginBand,
  getMarginStatus,
  getStatusLabel,
  isFiniteNumber,
} from './utils';

const EGS_CATEGORY = getChecklistCategory('egs');

interface Props {
  bookByDate?: string;
  campaignType?: string;
  channelManager?: string;
  hotelName: string;
  isLoadingProperty: boolean;
  isLoadingSurcharges: boolean;
  offerId: string;
  packages?: Array<App.Package>;
  propertyId?: string;
  propertyError?: unknown;
  propertyRoomTypes?: Array<App.RoomType>;
  surcharges: Array<SalesforceSurchargePeriod>;
  surchargesError?: unknown;
  travelToDate?: string;
  vendorId?: string;
}

export default function CMChecklistWorkspace({
  bookByDate,
  campaignType,
  channelManager,
  hotelName,
  isLoadingProperty,
  isLoadingSurcharges,
  offerId,
  packages,
  propertyId,
  propertyError,
  propertyRoomTypes,
  surcharges,
  surchargesError,
  travelToDate,
  vendorId,
}: Props) {
  const [showAutoApproved, setShowAutoApproved] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTabId, setActiveTabId] = useState<ChecklistTabId>('approvalQueue');
  const [expandedQueueItems, setExpandedQueueItems] = useState<Record<string, boolean>>({});
  const [editedMargins, setEditedMargins] = useState<EditedMarginValues>({});
  const [editedCapacities, setEditedCapacities] = useState<EditedCapacityValues>({});
  const [manualApprovals, setManualApprovals] = useState<ManualApprovalState>({});

  const baseMarginRows = useMemo(() => buildEgsMarginRows(surcharges), [surcharges]);
  const baseCapacityRows = useMemo(
    () => buildEgsCapacityRows({ packages, propertyRoomTypes, surcharges }),
    [packages, propertyRoomTypes, surcharges],
  );

  const marginRows = useMemo(
    () => applyEditedMarginRows(baseMarginRows, editedMargins),
    [baseMarginRows, editedMargins],
  );
  const capacityRows = useMemo(
    () => applyEditedCapacityRows(baseCapacityRows, editedCapacities),
    [baseCapacityRows, editedCapacities],
  );
  const marginRowsById = useMemo(() => new Map(marginRows.map((row) => [row.id, row])), [marginRows]);
  const capacityRowsById = useMemo(() => new Map(capacityRows.map((row) => [row.id, row])), [capacityRows]);

  useEffect(() => {
    setEditedMargins({});
    setEditedCapacities({});
    setManualApprovals({});
    setExpandedQueueItems({});
  }, [packages, propertyRoomTypes, surcharges]);

  const isApprovalQueueMode = activeTabId === 'approvalQueue';
  const activeCategory = isApprovalQueueMode ? EGS_CATEGORY : getChecklistCategory(activeTabId);
  const isEgsCategoryActive = activeCategory.id === 'egs';
  const visibleMarginRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isEgsCategoryActive || isApprovalQueueMode ? marginRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getMarginSearchText,
      }),
    [isApprovalQueueMode, isEgsCategoryActive, manualApprovals, marginRows, searchQuery, showAutoApproved],
  );
  const visibleCapacityRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isEgsCategoryActive || isApprovalQueueMode ? capacityRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getCapacitySearchText,
      }),
    [capacityRows, isApprovalQueueMode, isEgsCategoryActive, manualApprovals, searchQuery, showAutoApproved],
  );
  const visibleQueueItems = useMemo(
    () => [
      ...visibleMarginRows.map((row) => buildMarginQueueItem(row)),
      ...visibleCapacityRows.map((row) => buildCapacityQueueItem(row)),
    ],
    [visibleCapacityRows, visibleMarginRows],
  );
  const reportSummaries = useMemo(
    () =>
      buildReportSummaries({
        capacityRows,
        manualApprovals,
        marginRows,
        propertyId,
        vendorId,
      }),
    [capacityRows, manualApprovals, marginRows, propertyId, vendorId],
  );
  const areVisibleQueueItemsExpanded =
    visibleQueueItems.length > 0 && visibleQueueItems.every((queueItem) => expandedQueueItems[queueItem.id]);
  const summary = useChecklistSummary({ capacityRows, manualApprovals, marginRows });
  const isLoading = isLoadingProperty || isLoadingSurcharges;
  const hasError = !!propertyError || !!surchargesError;

  if (hasError) {
    return <Alert severity="error">Unable to load the CM checklist data.</Alert>;
  }

  if (isLoading) {
    return <Alert severity="info">Loading CM checklist data.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <ContextCard
        approvedCount={summary.approvedCount}
        bookByDate={bookByDate}
        campaignType={campaignType}
        channelManager={channelManager}
        hotelName={hotelName}
        needsApprovalCount={summary.needsApprovalCount}
        onSelectApprovalQueue={() => setActiveTabId('approvalQueue')}
        offerId={offerId}
        travelToDate={travelToDate}
        totalCount={summary.totalCount}
      />

      <ChecklistToolbar
        activeTabId={activeTabId}
        areVisibleQueueItemsExpanded={areVisibleQueueItemsExpanded}
        isApprovalQueueMode={isApprovalQueueMode}
        needsApprovalCount={summary.needsApprovalCount}
        searchQuery={searchQuery}
        showAutoApproved={showAutoApproved}
        visibleQueueItemCount={visibleQueueItems.length}
        onExpandVisibleCards={() =>
          setExpandedQueueItems((current) => toggleVisibleQueueItems(current, visibleQueueItems))
        }
        onSearchQueryChange={setSearchQuery}
        onShowAutoApprovedChange={() => setShowAutoApproved((current) => !current)}
        onTabChange={setActiveTabId}
      />

      {isApprovalQueueMode ? (
        <ApprovalQueue
          expandedQueueItems={expandedQueueItems}
          items={visibleQueueItems}
          needsApprovalCount={summary.needsApprovalCount}
          onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
          onToggleDetails={(itemId) => setExpandedQueueItems((current) => ({ ...current, [itemId]: !current[itemId] }))}
          reportSummaries={reportSummaries}
        />
      ) : isEgsCategoryActive ? (
        <>
          <MarginReportCard
            hiddenApprovedCount={marginRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleMarginRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
            onValueChange={(rowId, fieldName, value) =>
              setEditedMargins((current) => ({
                ...current,
                [rowId]: {
                  costPrice: current[rowId]?.costPrice ?? marginRowsById.get(rowId)?.costPrice,
                  sellPrice: current[rowId]?.sellPrice ?? marginRowsById.get(rowId)?.sellPrice,
                  [fieldName]: value,
                },
              }))
            }
          />

          <CapacityReportCard
            hiddenApprovedCount={capacityRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleCapacityRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
            onValueChange={(rowId, fieldName, value) =>
              setEditedCapacities((current) => ({
                ...current,
                [rowId]: {
                  egsCapacity: current[rowId]?.egsCapacity ?? capacityRowsById.get(rowId)?.egsCapacity,
                  includedCapacity: current[rowId]?.includedCapacity ?? capacityRowsById.get(rowId)?.includedCapacity,
                  maxRoomCapacity: current[rowId]?.maxRoomCapacity ?? capacityRowsById.get(rowId)?.maxRoomCapacity,
                  [fieldName]: value,
                },
              }))
            }
          />
        </>
      ) : (
        <PlaceholderChecklistTab category={activeCategory} />
      )}
    </Stack>
  );
}

function applyEditedMarginRows(rows: Array<EgsMarginChecklistRow>, editedMargins: EditedMarginValues) {
  return rows.map((row) => {
    const edited = editedMargins[row.id];
    if (!edited) {
      return row;
    }

    const costPrice = edited.costPrice;
    const sellPrice = edited.sellPrice;
    const margin = calculateMarginPercentage(costPrice, sellPrice);
    const marginBand = getMarginBand(margin);
    const status = getMarginStatus(marginBand);

    return {
      ...row,
      costPrice,
      margin,
      marginBand,
      sellPrice,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

function applyEditedCapacityRows(rows: Array<EgsCapacityChecklistRow>, editedCapacities: EditedCapacityValues) {
  return rows.map((row) => {
    const edited = editedCapacities[row.id];
    if (!edited) {
      return row;
    }

    const { egsCapacity, includedCapacity, maxRoomCapacity } = edited;
    const hasAllValues =
      isFiniteNumber(egsCapacity) && isFiniteNumber(includedCapacity) && isFiniteNumber(maxRoomCapacity);
    const status: ChecklistStatus = hasAllValues
      ? maxRoomCapacity === includedCapacity + egsCapacity
        ? 'approved'
        : 'needsApproval'
      : 'blocked';

    return {
      ...row,
      egsCapacity,
      includedCapacity,
      maxRoomCapacity,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

function useChecklistSummary({
  capacityRows,
  manualApprovals,
  marginRows,
}: {
  capacityRows: Array<EgsCapacityChecklistRow>;
  manualApprovals: ManualApprovalState;
  marginRows: Array<EgsMarginChecklistRow>;
}) {
  return useMemo(() => {
    const allRows = [...marginRows, ...capacityRows];
    const approvedCount = allRows.filter((row) => isRowApproved(row, manualApprovals)).length;
    const needsApprovalCount = allRows.length - approvedCount;

    return {
      approvedCount,
      needsApprovalCount,
      totalCount: allRows.length,
    };
  }, [capacityRows, manualApprovals, marginRows]);
}

function filterRows<Row>({
  isApproved,
  rows,
  searchQuery,
  showAutoApproved,
  text,
}: {
  isApproved: (row: Row) => boolean;
  rows: Array<Row>;
  searchQuery: string;
  showAutoApproved: boolean;
  text: (row: Row) => string;
}) {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  return rows.filter((row) => {
    if (!showAutoApproved && isApproved(row)) {
      return false;
    }

    return !normalizedSearchQuery || text(row).toLowerCase().includes(normalizedSearchQuery);
  });
}

function buildMarginQueueItem(row: EgsMarginChecklistRow): ApprovalQueueItem {
  return {
    categoryId: EGS_CATEGORY.id,
    categoryLabel: EGS_CATEGORY.label,
    description: `${row.name} has a ${row.marginBand ? `${row.marginBand} ` : ''}margin check and needs review.`,
    details: [
      { label: 'Opportunity', value: row.opportunityName || 'Opportunity not mapped' },
      { label: 'Cost price', value: formatMoney(row.costPrice, row.currencyCode) },
      { label: 'Sell price', value: formatMoney(row.sellPrice, row.currencyCode) },
      { label: 'Margin', value: typeof row.margin === 'number' ? `${row.margin.toFixed(1)}%` : 'N/A' },
      { label: 'Collected by', value: row.collectedBy || 'N/A' },
    ],
    id: row.id,
    title: row.name,
  };
}

function buildCapacityQueueItem(row: EgsCapacityChecklistRow): ApprovalQueueItem {
  const includedPlusEgs =
    isFiniteNumber(row.includedCapacity) && isFiniteNumber(row.egsCapacity)
      ? String(row.includedCapacity + row.egsCapacity)
      : 'N/A';

  return {
    categoryId: EGS_CATEGORY.id,
    categoryLabel: EGS_CATEGORY.label,
    description: `${row.name} capacity requires approval.`,
    details: [
      { label: 'Room asset', value: row.roomName },
      { label: 'Included capacity', value: formatOptionalNumber(row.includedCapacity) },
      { label: 'Max room capacity', value: formatOptionalNumber(row.maxRoomCapacity) },
      { label: 'EGS contracted for', value: formatOptionalNumber(row.egsCapacity) },
      { label: 'Included + EGS', value: includedPlusEgs },
    ],
    id: row.id,
    title: row.name,
  };
}

function buildReportSummaries({
  capacityRows,
  manualApprovals,
  marginRows,
  propertyId,
  vendorId,
}: {
  capacityRows: Array<EgsCapacityChecklistRow>;
  manualApprovals: ManualApprovalState;
  marginRows: Array<EgsMarginChecklistRow>;
  propertyId?: string;
  vendorId?: string;
}): Array<ApprovalReportSummary> {
  const egsRowsRequiringApproval = [...marginRows, ...capacityRows].filter(
    (row) => !isRowApproved(row, manualApprovals),
  );
  const egsSourceLinks = buildEgsSourceLinks({
    capacityRows,
    propertyId,
    vendorId,
  });

  if (egsRowsRequiringApproval.length === 0) {
    return [];
  }

  const egsRowCopy = `EGS row${egsRowsRequiringApproval.length === 1 ? '' : 's'}`;
  const egsReviewVerb = egsRowsRequiringApproval.length === 1 ? 'needs' : 'need';

  return [
    {
      categoryId: EGS_CATEGORY.id,
      categoryLabel: EGS_CATEGORY.label,
      count: egsRowsRequiringApproval.length,
      description: `${egsRowsRequiringApproval.length} ${egsRowCopy} still ${egsReviewVerb} review. Verify source data in AP room-rate capacity and surcharge setup.`,
      sourceLinks: egsSourceLinks,
    },
  ];
}

function buildEgsSourceLinks({
  capacityRows,
  propertyId,
  vendorId,
}: {
  capacityRows: Array<EgsCapacityChecklistRow>;
  propertyId?: string;
  vendorId?: string;
}) {
  if (!propertyId || !vendorId) {
    return [];
  }

  const sourceRow = capacityRows.find((row) => row.roomTypeId && row.roomRateId);

  if (!sourceRow?.roomTypeId || !sourceRow.roomRateId) {
    return [];
  }

  return [
    {
      href: `/vendors/${vendorId}/properties/${propertyId}/room-types/${sourceRow.roomTypeId}/room-rates/${sourceRow.roomRateId}`,
      label: 'Verify source data',
    },
  ];
}

function getChecklistCategory(categoryId: ChecklistCategoryId): ChecklistCategory {
  return CM_CHECKLIST_CATEGORIES.find((category) => category.id === categoryId) ?? CM_CHECKLIST_CATEGORIES[0];
}

function toggleVisibleQueueItems(
  current: Record<string, boolean>,
  visibleQueueItems: Array<ApprovalQueueItem>,
): Record<string, boolean> {
  const shouldExpand = !visibleQueueItems.every((queueItem) => current[queueItem.id]);

  return visibleQueueItems.reduce(
    (next, queueItem) => ({
      ...next,
      [queueItem.id]: shouldExpand,
    }),
    current,
  );
}

function isRowApproved(row: ChecklistRow, manualApprovals: ManualApprovalState) {
  return row.status === 'approved' || !!manualApprovals[row.id];
}

function getMarginSearchText(row: EgsMarginChecklistRow) {
  return [row.name, row.opportunityName, row.currencyCode, row.collectedBy, row.statusLabel].filter(Boolean).join(' ');
}

function getCapacitySearchText(row: EgsCapacityChecklistRow) {
  return [row.name, row.roomName, row.statusLabel].filter(Boolean).join(' ');
}
